import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import alertify from "alertifyjs";
import { Translate, I18n } from "react-redux-i18n";
import { Camera } from "theme/icon";
import { confirmDelete } from "utils/confirmDialog";
import * as actions from "redux/actions/noteActions";

function escapeHtml(value) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function toEditorHtml(text) {
    if (!text) {
        return "";
    }
    if (/<[a-z][\s\S]*>/i.test(text)) {
        return text;
    }
    return escapeHtml(text).replace(/\n/g, "<br>");
}

function normalizeEditorHtml(html) {
    const value = String(html || "")
        .replace(/&nbsp;/gi, " ")
        .trim();

    if (!value || value === "<br>" || value === "<div><br></div>") {
        return "";
    }
    return html;
}

const Body = (props) => {
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const lastEmittedRef = useRef(null);
    const previewNodeRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [previewSrc, setPreviewSrc] = useState(null);

    // The parent syncs `text` in its own effect, which runs after this one, so a
    // note switch delivers the previous text first. Compare against the last
    // value we emitted to pick up the real content without stealing the caret.
    useEffect(() => {
        if (!editorRef.current) {
            return;
        }
        if (props.text === lastEmittedRef.current) {
            return;
        }
        lastEmittedRef.current = props.text;
        editorRef.current.innerHTML = toEditorHtml(props.text);
    }, [props.noteId, props.text]);

    useEffect(() => {
        if (!previewSrc) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                previewNodeRef.current = null;
                setPreviewSrc(null);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [previewSrc]);

    const emitChange = () => {
        if (!editorRef.current) {
            return null;
        }
        const value = normalizeEditorHtml(editorRef.current.innerHTML);
        lastEmittedRef.current = value;
        if (props.onChange) {
            props.onChange({
                target: {
                    name: "text",
                    value,
                },
            });
        }
        return value;
    };

    const buildImageHtml = (url) => {
        const img = document.createElement("img");
        img.src = url;
        img.alt = "";
        img.className = "knowledge-note-image";
        return img;
    };

    // Selection can be lost while the native file dialog is open, so fall back to
    // appending at the end instead of dropping an already uploaded image.
    const insertImageAtCursor = (url) => {
        const editor = editorRef.current;
        if (!editor || !url) {
            return null;
        }

        const img = buildImageHtml(url);

        try {
            editor.focus();
            const selection = window.getSelection();
            let range = null;

            if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                range = selection.getRangeAt(0);
            } else {
                range = document.createRange();
                range.selectNodeContents(editor);
                range.collapse(false);
            }

            range.deleteContents();
            range.insertNode(img);

            const spacer = document.createElement("br");
            range.setStartAfter(img);
            range.collapse(true);
            range.insertNode(spacer);
            range.setStartAfter(spacer);
            range.collapse(true);

            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
            }
        } catch (error) {
            // handled by the containment check below
        }

        if (!editor.contains(img)) {
            editor.appendChild(img);
            editor.appendChild(document.createElement("br"));
        }

        return emitChange();
    };

    const uploadFile = async (file) => {
        if (!file || !props.user_id) {
            return;
        }
        if (!String(file.type || "").startsWith("image/")) {
            alertify.error(I18n.t("note_image_invalid"));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alertify.error(I18n.t("note_image_too_large"));
            return;
        }

        setUploading(true);
        let uploadedUrl = null;
        try {
            const result = await props.uploadImage({
                id: props.user_id,
                file,
            }, props.token);
            uploadedUrl = result?.url || null;
        } catch (error) {
            alertify.error(I18n.t("note_image_upload_failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }

        if (!uploadedUrl) {
            return;
        }

        const html = insertImageAtCursor(uploadedUrl);
        if (!html || html.indexOf(uploadedUrl) === -1) {
            alertify.error(I18n.t("note_image_insert_failed"));
            return;
        }

        // Persist right away: the image lives inside the note HTML, so losing the
        // editor before an explicit Save would drop it.
        persist(html);
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) {
            uploadFile(file);
        }
    };

    const handlePaste = (event) => {
        const items = event.clipboardData && event.clipboardData.items;
        if (!items) {
            return;
        }

        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            if (item.type && item.type.startsWith("image/")) {
                event.preventDefault();
                const file = item.getAsFile();
                if (file) {
                    uploadFile(file);
                }
                return;
            }
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (file) {
            uploadFile(file);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const persist = (html) => {
        if (html != null && props.onImageInserted) {
            props.onImageInserted(html);
        }
    };

    const closePreview = () => {
        previewNodeRef.current = null;
        setPreviewSrc(null);
    };

    // Thumbnails live inside the contentEditable, so intercept the click before
    // the browser turns it into an image selection.
    const handleEditorClick = (event) => {
        const target = event.target;
        if (!target || target.tagName !== "IMG") {
            return;
        }
        event.preventDefault();
        previewNodeRef.current = target;
        setPreviewSrc(target.currentSrc || target.src);
    };

    const handleDeleteImage = () => {
        const node = previewNodeRef.current;
        const editor = editorRef.current;
        if (!node || !editor || !editor.contains(node)) {
            closePreview();
            return;
        }

        confirmDelete(I18n.t("note_image_delete_confirm"), () => {
            const next = node.nextSibling;
            if (next && next.nodeName === "BR") {
                next.remove();
            }
            node.remove();
            closePreview();
            persist(emitChange());
            alertify.success(I18n.t("note_image_deleted"));
        });
    };

    return (
        <div className="knowledge-note-editor-wrap">
            <div className="knowledge-note-toolbar">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="knowledge-note-file-input"
                    id="knowledge-note-image-input"
                    onChange={handleFileChange}
                    disabled={uploading || !props.noteId}
                />
                <label
                    htmlFor="knowledge-note-image-input"
                    className={`btn btn-secondary knowledge-note-image-btn${uploading || !props.noteId ? " is-disabled" : ""}`}
                >
                    <Camera />
                    <span>
                        <Translate value={uploading ? "note_image_uploading" : "note_add_image"} />
                    </span>
                </label>
            </div>
            <div
                ref={editorRef}
                className={`knowledge-editor knowledge-editor--rich${!props.noteId ? ' is-disabled' : ''}`}
                contentEditable={Boolean(props.noteId)}
                role="textbox"
                aria-multiline="true"
                aria-disabled={!props.noteId}
                suppressContentEditableWarning
                data-placeholder={I18n.t("note_editor_placeholder")}
                onInput={emitChange}
                onBlur={emitChange}
                onPaste={handlePaste}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleEditorClick}
            />

            {previewSrc && (
                <div
                    className="knowledge-image-lightbox"
                    role="presentation"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            closePreview();
                        }
                    }}
                >
                    <div
                        className="knowledge-image-lightbox-inner"
                        role="dialog"
                        aria-modal="true"
                        aria-label={I18n.t("note_image_preview")}
                    >
                        <div className="knowledge-image-lightbox-actions">
                            <button
                                type="button"
                                className="btn btn-danger btn-sm knowledge-image-lightbox-btn"
                                onClick={handleDeleteImage}
                                disabled={!props.noteId}
                            >
                                <Translate value="note_image_delete" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-text btn-sm knowledge-image-lightbox-btn"
                                onClick={closePreview}
                            >
                                <Translate value="close" />
                            </button>
                        </div>
                        <img
                            className="knowledge-image-lightbox-img"
                            src={previewSrc}
                            alt={I18n.t("note_image_preview")}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

Body.propTypes = {
    className: PropTypes.string,
    history: PropTypes.object,
    onChange: PropTypes.func,
    text: PropTypes.string,
    noteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    uploadImage: PropTypes.func,
    onImageInserted: PropTypes.func,
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
        list: reducer.note.list,
        loading: reducer.note.loading,
        error: reducer.note.error,
        pageCount: reducer.note.page_count,
        pageSize: reducer.note.page_size,
        loadingMore: reducer.note.loadingMore,
    };
}

const mapDispatchToProps = (dispatch) => ({
    uploadImage: (params, token) =>
        dispatch(actions.uploadImage({ params, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Body);
